import { forwardRef, useState } from 'react';
import { Pressable, View } from 'react-native';

import {
  addThousandSeparator,
  formatAssetAmount,
  getAssetPrecision,
  getFiatSymbol,
} from '@altify/utils';
import { useControllableState } from '@rn-primitives/hooks';
import { Button } from '../../../../Core/Button/Button';
import { TextField, TextFieldRef } from '../../../../Core/TextInput/TextField';
import { MinusIcon } from '../../../../Icons/MinusIcon';
import { PlusIcon } from '../../../../Icons/PlusIcon';
import { cn } from '../../../../lib/utils';
import { AssetAmountInputProps } from '../../AssetField.types';
import { mapValueLengthToFontSize } from './fontSizeMapping';

const states = {
  default: 'border border-neutral-500 hover:border-primary',
  disabled: 'border border-neutral-500',
  focused: 'border-2 border-primary',
  error: 'border-2 border-error',
} as const;

export const AssetAmountInput = forwardRef<TextFieldRef, AssetAmountInputProps>(
  (
    {
      value,
      className,
      onValueChanged,
      onMinusPressed,
      onPlusPressed,
      hasError = false,
      showPlusMinusButtons,
      readonly,
      assetCode,
      percentageButtons,
      onPercentageButtonPressed,
      onFocus,
      onBlur,
    },
    ref,
  ) => {
    const [internalValue, handleValueChange] = useControllableState({
      prop: value,
      onChange: onValueChanged,
      defaultProp: undefined,
    });

    const [isFocused, setIsFocused] = useState(false);

    // Since a number that ends with a comma or a 0 (e.g. '10.', '10.0'), which happens during typing
    // will normally be truncated when converting it to a number, we need to add back this characters manually
    // Better ideas welcome ;)
    const [suffix, setSuffix] = useState('');

    const placeholder = assetCode
      ? formatAssetAmount(0, assetCode, { showCryptoCode: false })
      : '0';

    function getValueToShow() {
      if (internalValue == null || assetCode == null) return '';

      if (readonly) {
        return `≈ ${formatAssetAmount(internalValue, assetCode, {
          showCryptoCode: false,
          showSpacer: true,
        })}`;
      } else {
        // We cannot use formatAssetAmount here since the user might still be in the progress of typing and we e.g.
        // don't want to format a '1.1' to a '1.10'
        const assetSymbol = getFiatSymbol(assetCode);
        return `${assetSymbol}${addThousandSeparator(internalValue)}${suffix}`;
      }
    }

    const handleValueChanged = (newValue: string) => {
      if (readonly || assetCode == null) {
        return;
      }

      const maxDecimals = getAssetPrecision(assetCode);
      const assetSymbol = getFiatSymbol(assetCode);
      const valueWithoutPrefix = newValue.startsWith(assetSymbol)
        ? newValue.slice(assetSymbol.length)
        : newValue;

      let numericOnlyAmount = valueWithoutPrefix
        .replace(/[,]/g, '.')
        .replace(/[^0-9.]/g, '');

      const parts = numericOnlyAmount.split('.');
      //this is to ensure no more than one '.' exists
      if (parts.length > 2) {
        numericOnlyAmount = `${parts[0]}.${parts.slice(1).join('')}`;
      }

      if (numericOnlyAmount === '.') {
        return;
      }

      //limit the number of decimals based on precision
      const newParts = numericOnlyAmount.split('.');
      const containsComma = newParts.length === 2;
      if (containsComma) {
        const limitedDecimals = newParts[1].substring(0, maxDecimals);
        numericOnlyAmount = `${parts[0]}.${limitedDecimals}`;
      }

      // Numbers larger than this cannot be properly represented with JS floating point numbers
      const maxLength = containsComma ? 16 : 15;
      if (numericOnlyAmount.length > maxLength) {
        numericOnlyAmount = numericOnlyAmount.substring(0, maxLength);
      }

      /*
        This matches:
        - the trailing comma of a number (e.g. '.' in '0.' or '10.')
        - the trailing comma and 0s of a number (e.g. '.0' in '10.0' or '.00' in '10.00')
        - trailing zeros but only if they part of the decimal places of a number (e.g. '0' in '10.10' or '1.010' but not in '10')
      */
      const zerosAfterComma = numericOnlyAmount.match(
        /\.0+$|(?<=\.\d+)0+$|\.$/,
      );
      if (zerosAfterComma) {
        setSuffix(zerosAfterComma[0]);
      } else {
        setSuffix('');
      }

      handleValueChange(
        numericOnlyAmount !== '' ? Number(numericOnlyAmount) : null,
      );
    };

    const getState = (hasError: boolean, isFocused: boolean) => {
      if (hasError) return 'error';
      if (isFocused) return 'focused';
      return 'default';
    };

    const state = getState(hasError, isFocused);
    const border = states[readonly ? 'disabled' : state];
    const valueToShow = getValueToShow();

    return (
      <Pressable
        onPress={() => {
          if (!readonly) {
            setIsFocused(true);
          }
        }}
        className={cn(
          'h-[56px] bg-neutral-600 items-center rounded-br-2xl rounded-tr-2xl flex-row p-4',
          border,
          className,
        )}>
        {showPlusMinusButtons && (
          <Pressable
            onPress={onMinusPressed}
            className="active:opacity-20 mr-3">
            <MinusIcon />
          </Pressable>
        )}
        <View className="flex-row items-center flex-1">
          <TextField
            inputMode="decimal"
            ref={ref}
            fontSize={mapValueLengthToFontSize(valueToShow.length)}
            keyboardType={'decimal-pad'}
            onValueChanged={handleValueChanged}
            onFocus={() => {
              onFocus?.();
              if (!readonly) {
                setIsFocused(true);
              }
            }}
            onBlur={() => {
              onBlur?.();
              setIsFocused(false);
            }}
            value={valueToShow}
            isFocused={isFocused}
            className="h-full w-full"
            placeholder={placeholder}
            readOnly={readonly}
            keyboardOverlay={
              percentageButtons == null ? undefined : (
                <View className="flex-row gap-2 px-6 py-3">
                  {percentageButtons.map(e => (
                    <Button
                      size="medium"
                      key={e}
                      className="flex-1"
                      color="gray"
                      text={e + '%'}
                      onPress={() => onPercentageButtonPressed?.(e)}
                    />
                  ))}
                </View>
              )
            }
          />
        </View>
        {showPlusMinusButtons && (
          <Pressable onPress={onPlusPressed} className="ml-3 active:opacity-20">
            <PlusIcon />
          </Pressable>
        )}
      </Pressable>
    );
  },
);
